import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Skeleton,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Inventory,
  ShoppingCart,
  LocalShipping,
  Warning,
  Category,
  People,
  Assignment,
  NewReleases,
  ReceiptLong,
  ArrowForward,
  PointOfSale,
  AccountBalanceWallet,
  CreditCard,
  Payments,
  AccessTime,
  Person,
  CheckCircle,
  LockClock,
  Assessment,
} from '@mui/icons-material';
import { useGetDashboardSummaryQuery } from '../../../store/api/dashboardApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetActiveRegisterSessionQuery } from '../../../store/api/salesApi';
import { usePermission } from '../../../hooks/usePermission';

function formatAmount(value, currency = 'USD', decimals = 2) {
  const num = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  } catch {
    return `${currency} ${num.toFixed(decimals)}`;
  }
}

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

function formatTime(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasModuleAccess } = usePermission();
  const canPurchases = hasModuleAccess('purchases');
  const canSales = hasModuleAccess('sales');
  const canProducts = hasModuleAccess('products');
  const canInventory = hasModuleAccess('inventory');
  const canCustomers = hasModuleAccess('sales');

  const { data, isLoading, error } = useGetDashboardSummaryQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = data?.currency || company?.currency || 'AED';
  const decimals = company?.currencyDecimals ?? 2;

  // Active Register Shift Query
  const { data: activeSessionData } = useGetActiveRegisterSessionQuery(undefined, {
    skip: !company?.enableSalesSession,
    pollingInterval: 30000,
  });

  const session = activeSessionData?.id ? activeSessionData : activeSessionData?.session;
  const isShiftOpen = !!(session && (session.status === 'open' || session.id));

  // Filters (shift, today, month, all)
  const isShiftEnabled = !!company?.enableSalesSession;
  const availableFilters = isShiftEnabled ? ['shift', 'today', 'month', 'all'] : ['today', 'month', 'all'];

  const [salesFilter, setSalesFilter] = useState(isShiftEnabled ? 'today' : 'today');
  const [salesCardFilter, setSalesCardFilter] = useState(isShiftEnabled ? 'shift' : 'today');

  const summary = data?.summary || {};
  const lowStockItems = data?.lowStockItems || [];
  const recentActivity = data?.recentActivity || [];

  // Top KPI Sales Card calculation
  let salesValue = summary.totalSales;
  let salesSub = summary.salesCount != null ? `${summary.salesCount} orders` : null;
  let salesTitle = 'Total Sales';

  if (salesFilter === 'shift') {
    salesValue = session?.financials?.totalSales || 0;
    salesSub = isShiftOpen
      ? `${session?.financials?.salesCount || 0} shift orders • Float: ${formatAmount(session?.openingBalance, currency, decimals)}`
      : 'Shift Closed (No Active Shift)';
    salesTitle = 'Current Shift Sales';
  } else if (salesFilter === 'month') {
    salesValue = summary.salesThisMonth;
    salesSub = summary.salesCountThisMonth != null ? `${summary.salesCountThisMonth} orders` : null;
    salesTitle = 'Total Sales (Month)';
  } else if (salesFilter === 'today') {
    salesValue = summary.salesToday;
    salesSub = summary.salesCountToday != null ? `${summary.salesCountToday} orders` : null;
    salesTitle = "Today's Total Sales";
  }

  const statCards = [
    {
      title: 'Total Products',
      value: summary.totalProducts ?? '—',
      sub: summary.activeProducts != null ? `${summary.activeProducts} active` : null,
      icon: <Inventory sx={{ fontSize: 36 }} />,
      color: theme.palette.primary.main,
      path: canProducts ? '/products' : null,
    },
    {
      title: salesTitle,
      value: formatAmount(salesValue, currency, decimals),
      sub: salesSub,
      icon: <ShoppingCart sx={{ fontSize: 36 }} />,
      color: theme.palette.success.main,
      path: canSales ? '/sales' : null,
      isSalesCard: true,
    },
    {
      title: 'Total Purchases',
      value: formatAmount(summary.totalPurchases, currency, decimals),
      sub: summary.purchaseCount != null ? `${summary.purchaseCount} orders` : null,
      icon: <LocalShipping sx={{ fontSize: 36 }} />,
      color: theme.palette.warning.main,
      path: canPurchases ? '/purchases' : null,
    },
    {
      title: 'Low Stock Items',
      value: summary.lowStockCount ?? 0,
      sub: summary.lowStockCount > 0 ? 'Need attention' : null,
      icon: <Warning sx={{ fontSize: 36 }} />,
      color: theme.palette.error.main,
      path: canInventory ? '/inventory' : null,
    },
  ];

  const quickActions = [
    { label: 'Add Product', icon: <Inventory />, path: canProducts ? '/products/new' : null },
    { label: 'POS / New Sale (F2)', icon: <PointOfSale />, path: canSales ? '/sales/orders/new' : null },
    { label: 'Purchase Order', icon: <LocalShipping />, path: canPurchases ? '/purchases/orders/new' : null },
    { label: 'Categories', icon: <Category />, path: canProducts ? '/products/categories' : null },
    { label: 'Inventory', icon: <Inventory />, path: canInventory ? '/inventory' : null },
    { label: 'Customers', icon: <People />, path: canCustomers ? '/customers' : null },
  ].filter((a) => a.path);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load dashboard. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here is an overview of your business activity and sales.
          </Typography>
        </Box>
      </Box>

      {/* ── POS Register Shift & Today's Performance Hub ────────────── */}
      {isShiftEnabled && (
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: isShiftOpen ? '#bbf7d0' : '#e2e8f0',
            background: isShiftOpen
              ? 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            {/* Shift Header Row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 1.5,
                mb: 2,
                pb: 1.5,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  icon={
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: isShiftOpen ? '#10b981' : '#94a3b8',
                        ml: '8px !important',
                      }}
                    />
                  }
                  label={isShiftOpen ? 'Active POS Register Shift' : 'POS Register Shift Closed'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: isShiftOpen ? '#dcfce7' : '#f1f5f9',
                    color: isShiftOpen ? '#15803d' : '#64748b',
                    border: `1px solid ${isShiftOpen ? '#86efac' : '#cbd5e1'}`,
                    height: 26,
                  }}
                />

                {isShiftOpen && session?.user && (
                  <Typography variant="body2" sx={{ color: '#475569', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person sx={{ fontSize: 16, color: '#0ea5e9' }} /> Cashier:{' '}
                    <strong>{session.user.firstName} {session.user.lastName || ''}</strong>
                  </Typography>
                )}

                {isShiftOpen && session?.openedAt && (
                  <Typography variant="body2" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 16, color: '#64748b' }} /> Opened at:{' '}
                    {formatTime(session.openedAt)}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PointOfSale />}
                  onClick={() => navigate('/sales/orders/new')}
                  sx={{
                    bgcolor: '#0ea5e9',
                    '&:hover': { bgcolor: '#0284c7' },
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  {isShiftOpen ? 'Open POS Terminal (F2)' : 'Start New Shift Session'}
                </Button>
              </Box>
            </Box>

            {/* Metrics Breakdown */}
            <Grid container spacing={2}>
              {/* 1. Opening Cash Float */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <AccountBalanceWallet sx={{ fontSize: 16, color: '#f59e0b' }} /> Opening Cash Float
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mt: 0.5 }}>
                    {formatAmount(session?.openingBalance || 0, currency, decimals)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {isShiftOpen ? 'Drawer baseline at shift open' : 'No shift open'}
                  </Typography>
                </Paper>
              </Grid>

              {/* 2. Current Shift Sales */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <ShoppingCart sx={{ fontSize: 16, color: '#10b981' }} /> Current Shift Sales
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#15803d', mt: 0.5 }}>
                    {formatAmount(session?.financials?.totalSales || 0, currency, decimals)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {session?.financials?.salesCount || 0} bills • Cash: {formatAmount(session?.financials?.cashSales || 0, currency, decimals)}
                  </Typography>
                </Paper>
              </Grid>

              {/* 3. Estimated Drawer Cash in Hand */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Payments sx={{ fontSize: 16, color: '#0ea5e9' }} /> Cash in Drawer (Expected)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0369a1', mt: 0.5 }}>
                    {formatAmount(
                      session?.financials?.expectedClosingCash ?? (session?.openingBalance || 0),
                      currency,
                      decimals
                    )}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Card: {formatAmount(session?.financials?.cardSales || 0, currency, decimals)} • Credit: {formatAmount(session?.financials?.creditSales || 0, currency, decimals)}
                  </Typography>
                </Paper>
              </Grid>

              {/* 4. Today's Total Store Sales */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Assessment sx={{ fontSize: 16, color: '#6366f1' }} /> Today's Total Store Sales
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#4338ca', mt: 0.5 }}>
                    {formatAmount(summary.salesToday, currency, decimals)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {summary.salesCountToday ?? 0} store order(s) today
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card
              onClick={stat.path ? () => navigate(stat.path) : undefined}
              sx={{
                height: '100%',
                cursor: stat.path ? 'pointer' : 'default',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': stat.path
                  ? {
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    }
                  : {},
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 2.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {stat.title}
                  </Typography>
                  {isLoading ? (
                    <Skeleton variant="text" width={120} height={36} />
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                  )}
                  {stat.sub && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {stat.sub}
                    </Typography>
                  )}
                  {stat.isSalesCard && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                      {availableFilters.map((filter) => (
                        <Chip
                          key={filter}
                          label={
                            filter === 'shift'
                              ? 'Shift'
                              : filter === 'all'
                              ? 'All'
                              : filter === 'month'
                              ? 'Month'
                              : 'Today'
                          }
                          size="small"
                          color={salesFilter === filter ? 'success' : 'default'}
                          variant={salesFilter === filter ? 'filled' : 'outlined'}
                          onClick={() => setSalesFilter(filter)}
                          sx={{
                            fontSize: '0.65rem',
                            height: 18,
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${stat.color}20`,
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={1.5}>
              {quickActions.map((action, index) => (
                <Grid size={{ xs: 6, sm: 4 }} key={index}>
                  <Card
                    onClick={() => action.path && navigate(action.path)}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'translateY(-2px)',
                        boxShadow: 1,
                      },
                    }}
                  >
                    <Box sx={{ color: 'primary.main', mb: 0.5 }}>{action.icon}</Box>
                    <Typography variant="body2" fontWeight={600}>
                      {action.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Sales Performance Card */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {salesCardFilter === 'shift'
                  ? 'Current Shift Sales'
                  : salesCardFilter === 'all'
                  ? 'Sales All Time'
                  : salesCardFilter === 'month'
                  ? 'Sales This Month'
                  : "Today's Sales"}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {availableFilters.map((filter) => (
                  <Chip
                    key={filter}
                    label={
                      filter === 'shift'
                        ? 'Shift'
                        : filter === 'all'
                        ? 'All'
                        : filter === 'month'
                        ? 'Month'
                        : 'Today'
                    }
                    size="small"
                    color={salesCardFilter === filter ? 'success' : 'default'}
                    variant={salesCardFilter === filter ? 'filled' : 'outlined'}
                    onClick={() => setSalesCardFilter(filter)}
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </Box>
            {isLoading ? (
              <Skeleton variant="text" width="80%" height={48} sx={{ my: 2 }} />
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {formatAmount(
                    salesCardFilter === 'shift'
                      ? (session?.financials?.totalSales || 0)
                      : salesCardFilter === 'all'
                      ? summary.totalSales
                      : salesCardFilter === 'month'
                      ? summary.salesThisMonth
                      : summary.salesToday,
                    currency,
                    decimals
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {salesCardFilter === 'shift'
                    ? `${session?.financials?.salesCount || 0} shift order(s)`
                    : `${(salesCardFilter === 'all' ? summary.salesCount : salesCardFilter === 'month' ? summary.salesCountThisMonth : summary.salesCountToday) ?? 0} order(s)`}
                </Typography>

                {salesCardFilter === 'shift' && isShiftOpen && (
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-around' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Opening Float</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatAmount(session?.openingBalance || 0, currency, decimals)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Cash in Drawer</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0ea5e9' }}>
                        {formatAmount(session?.financials?.expectedClosingCash || session?.openingBalance || 0, currency, decimals)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {canSales && (
                  <Button
                    size="small"
                    startIcon={<ArrowForward />}
                    onClick={() => navigate('/sales')}
                    sx={{ mt: 2 }}
                  >
                    View Sales
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* New Features */}
        {canPurchases && (
          <Grid size={{ xs: 12 }}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'success.50',
                border: '1px solid',
                borderColor: 'success.200',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NewReleases sx={{ color: 'success.main', fontSize: 24 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  New Features
                </Typography>
              </Box>
              <Card
                onClick={() => navigate('/purchases/orders/new?lpo=1')}
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  cursor: 'pointer',
                  maxWidth: 320,
                  '&:hover': { bgcolor: 'success.100', boxShadow: 1 },
                }}
              >
                <Assignment sx={{ color: 'success.main', fontSize: 32 }} />
                <Box>
                  <Typography fontWeight={600}>Create LPO</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Local Purchase Order with reference number
                  </Typography>
                </Box>
              </Card>
            </Paper>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Activity
            </Typography>
            {isLoading ? (
              [1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />)
            ) : recentActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No recent activity
              </Typography>
            ) : (
              <List disablePadding>
                {recentActivity.slice(0, 8).map((item, index) => (
                  <ListItem
                    key={`${item.type}-${item.id}-${index}`}
                    disablePadding
                    sx={{
                      py: 1,
                      borderBottom: index < 7 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.type === 'sale' ? (
                        <ReceiptLong sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <LocalShipping sx={{ color: 'warning.main', fontSize: 20 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {item.description} · {formatAmount(item.amount, currency, decimals)}
                        </Typography>
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.date)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Low Stock */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Low Stock
              </Typography>
              {lowStockItems.length > 0 && canInventory && (
                <Button size="small" onClick={() => navigate('/inventory')}>
                  View all
                </Button>
              )}
            </Box>
            {isLoading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />)
            ) : lowStockItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No low stock items
              </Typography>
            ) : (
              <List disablePadding>
                {lowStockItems.slice(0, 6).map((item, index) => (
                  <ListItem
                    key={`${item.productId}-${item.locationName}-${index}`}
                    disablePadding
                    sx={{
                      py: 1.25,
                      borderBottom: index < 5 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.productName}
                      secondary={`${item.locationName} · Qty: ${item.quantity} (reorder: ${item.reorderPoint})`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip label="Low" size="small" color="warning" variant="outlined" />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
